// REACT IMPORTS
import EyeSlashIcon from "icons/auth/EyeSlashIcon";
import EyeIcon from "icons/auth/EyeIcon";
import React, { useEffect, useRef, useState } from "react";
import { formatCurrency } from "utils/HelperFunctions";
import { activateOnKey } from "utils/activateOnKey";

// PROPS TYPE
interface InputFieldProps {
  showLabel?: boolean;
  label?: string;
  name: string;
  placeholder?: string;
  value?: string | undefined;
  type?: string | undefined;
  disabled?: boolean;
  textarea?: boolean;
  onChange?: (e?: any) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e?: any) => void;
  onClick?: () => void;
  isErr?: boolean;
  error?: string;
  maxLength?: number;
  className?: string;
  _className?: string;
  min?: number;
  promo?: boolean;
  onClickApply?: any;
  tabInd?: number;
  onMouseEnter?: any;
  onMouseLeave?: any;
  customStyle?: any;
  "data-testid"?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

/* ------------------------------------- INPUT ------------------------------------- */

const InputField = (props: InputFieldProps) => {
  const {
    showLabel = true,
    label = "",
    name,
    value,
    type = "text",
    placeholder = "Enter",
    onChange,
    onBlur,
    onFocus,
    onClick,
    disabled = false,
    isErr = false,
    error,
    maxLength,
    textarea,
    onKeyDown,
    className = "",
    _className = "",
    min,
    promo = false,
    onClickApply,
    tabInd = undefined,
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    customStyle = {},
    ...inputProps
  } = props;
  const quantityInputRef = useRef<any>(null);

  useEffect(() => {
    const ignoreScroll = (e: any) => {
      e.preventDefault();
    };
    quantityInputRef?.current &&
      quantityInputRef?.current?.addEventListener("wheel", ignoreScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityInputRef]);
  const [passwordType, setPasswordType] = useState<boolean>(true);

  useEffect(() => {
    if (isErr) {
      quantityInputRef.current.focus();
    }
  }, [isErr]);

  return (
    <>
      <div className="relative flex flex-col gap-[3px] items-start">
        <div className="absolute bottom-[-20px] text-xs text-red">{error}</div>
        {showLabel && (
          <div
            className={`lg:text-base text-xs text-txtSecondary font-semibold leading-normal uppercase ${_className}`}
          >
            {label}
          </div>
        )}
        <div className="relative w-full">
          {textarea ? (
            <textarea
              ref={quantityInputRef}
              onBlur={onBlur}
              className={`w-full border-strokePrimary hover:!border-strokeActive   rounded-[4px] border lg:py-4 py-3 px-[15px] bg-bgPrimary lg:text-base text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                className ? className : ""
              }`}
              style={customStyle}
              placeholder={placeholder}
              name={name}
              onChange={onChange}
              onClick={onClick}
              value={value}
              onFocus={onFocus}
              disabled={disabled}
              maxLength={maxLength ? maxLength : undefined}
              rows={7} // You can customize this or add it as a prop
              cols={50} // You can customize this or add it as a prop
              {...inputProps}
            />
          ) : type && type === "password" ? (
            <div className="relative">
              <input
                ref={quantityInputRef}
                onBlur={onBlur}
                type={passwordType ? "password" : "text"}
                autoComplete="off"
                className={`w-full border-strokePrimary hover:!border-strokeActive  rounded-[4px] border lg:py-4 py-3 px-[15px] bg-bgPrimary lg:text-base text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                  className ? className : ""
                }`}
                placeholder={placeholder}
                name={name}
                onChange={onChange}
                onClick={onClick}
                value={value}
                onFocus={onFocus}
                disabled={disabled}
                maxLength={maxLength ? maxLength : undefined}
                onKeyDown={onKeyDown}
                min={min}
                tabIndex={tabInd}
                {...inputProps}
              />
              <div
                className="absolute bottom-0 right-0 lg:p-4 p-3 cursor-pointer"
                onClick={() => setPasswordType((state) => !state)}
                onKeyDown={activateOnKey(() =>
                  setPasswordType((state) => !state)
                )}
                role="button"
                tabIndex={0}
                aria-label={passwordType ? "Show password" : "Hide password"}
                data-testid={
                  inputProps["data-testid"]
                    ? `${inputProps["data-testid"]}-visibility-toggle`
                    : undefined
                }
              >
                {passwordType ? (
                  <EyeIcon aria-hidden="true" />
                ) : (
                  <EyeSlashIcon aria-hidden="true" />
                )}
              </div>
            </div>
          ) : promo ? (
            <>
              <input
                ref={quantityInputRef}
                onBlur={onBlur}
                type={type}
                autoComplete="off"
                className={`w-full border-strokePrimary hover:!border-strokeActive  rounded-[4px] border lg:py-4 py-3 px-[15px] bg-bgPrimary lg:text-base text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                  className ? className : ""
                }`}
                placeholder={placeholder}
                name={name}
                onChange={onChange}
                value={value}
                onFocus={onFocus}
                disabled={disabled}
                maxLength={maxLength ? maxLength : undefined}
                onKeyDown={onKeyDown}
                min={min}
                tabIndex={tabInd}
                {...inputProps}
              />
              {value?.trim() !== "" && (
                <div
                  className="cursor-pointer absolute bottom-0 right-0 text-TextBlue2 hover:!text-TextBlue2Hover h-full text-xs grid place-items-center pr-[11px]"
                  onClick={onClickApply}
                  onKeyDown={activateOnKey(onClickApply)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Apply ${label || name}`}
                  data-testid={
                    inputProps["data-testid"]
                      ? `${inputProps["data-testid"]}-apply`
                      : undefined
                  }
                >
                  Apply
                </div>
              )}
            </>
          ) : (
            <input
              ref={quantityInputRef}
              onBlur={onBlur}
              type={type}
              autoComplete="off"
              className={`w-full ${
                isErr
                  ? "!border-red"
                  : "border-strokePrimary hover:!border-strokeActive"
              } ${disabled ? "!bg-bgThird !border-strokePrimary" : ""} rounded-[4px] border lg:py-[15px] py-3 px-[15px] bg-bgPrimary lg:text-base text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                className ? className : ""
              }`}
              placeholder={placeholder}
              name={name}
              onChange={onChange}
              onClick={onClick}
              value={value}
              onFocus={onFocus}
              disabled={disabled}
              maxLength={maxLength ? maxLength : undefined}
              onKeyDown={onKeyDown}
              min={min}
              tabIndex={tabInd}
              onMouseOver={onMouseEnter}
              onMouseOut={onMouseLeave}
              {...inputProps}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default InputField;
